import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { AlertColor } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { ApplicationSectionCard } from '../components/application/applicationSectionChrome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type {
  ApplicationJourneyExecutionDto,
  ApplicationJourneyHeaderDto,
  ApplicationDeviceRiskResponseDto,
  ApplicationOnboardingSignalsDto,
  ApplicationTimelineEventDto,
  ApplicationVerificationSnapshotErrorDto,
} from '../api/admin';
import {
  shouldShowEmployeeDecisionRecordOnDecisionTab,
  type ApplicationTabKey,
} from '../lib/applicationDetailTabStatus';
import { IdentityMatchInsightsGrid } from '../components/application/IdentityMatchInsightsGrid';
import { IdentityPostMatchInsightsPanels } from '../components/application/IdentityPostMatchInsightsPanels';
import { ApplicationIdentityAttemptCard } from '../components/application/ApplicationIdentitySessionCard';
import {
  ApplicationMitekVerificationPanel,
  pickMitekVerificationRows,
} from '../components/application/ApplicationMitekVerificationPanel';
import { buildIdentityMatchInsights } from '../lib/identityMatchInsights';
import { buildIdentityPostMatchInsights } from '../lib/identityPostMatchInsights';
import {
  confidenceBucketLabel,
  firstAttentionGateKey,
  gateStatusChip,
  GateOutcomePickerChips,
  normalizedConfidenceScore,
  recommendedNextStepCopy,
  splitRecommendedActionsForDrawer,
} from '../components/verification/EmployerInvestigationGateSupport';
import { formatVerifiedRelativeRail } from '../lib/formatEmployerVerifiedRelative';
import { employerVerificationFinalChipColor } from '../lib/employerVerificationFinalChip';
import {
  finalOutcomeLabelStored,
  finalOutcomeTechnical,
  gateMeta,
  GATE_ORDER_PIPE,
  gateStatusShort,
  type GateKey,
} from '../lib/employerVerificationCopy';
import { summaryHeadlineFromFailReason } from '../lib/employerVerificationGateReasonFormat';
import { identityVerificationDashboardApi } from '../api/identityVerificationDashboardApi';
import { resolveIdentityLookupForApplication } from '../lib/identityApplicationLookup';
import { employerVerificationDashboardApi } from '../api/employerVerificationDashboardApi';
import { ApplicationDecisionEnginePanel } from '../components/verification/ApplicationDecisionEnginePanel';
import { PdlDecisionTourProvider } from '../components/verification/guidedTour/PdlDecisionTourContext';
import { PdlDecisionReviewPathTour } from '../components/verification/guidedTour/PdlDecisionReviewPathTour';
import { isPdlLoanType } from '../lib/pdlInterestRateDisplay';
import { ApplicationFusedCapacityPanel } from '../components/verification/ApplicationFusedCapacityPanel';
import { ApplicationLegacyLinkagePanel } from '../components/verification/ApplicationLegacyLinkagePanel';
import { decisionEnginePowerIdMismatch } from '../lib/legacyLinkageDisplay';
import { formatDecisionEngineMismatchCopy } from '../lib/legacyLinkageCopy';
import { ApplicationEmployeeDecisionPanel } from '../components/verification/ApplicationEmployeeDecisionPanel';
import { EmployerAttemptDetailAccordion } from '../components/verification/EmployerAttemptDetailAccordion';
import {
  diffRequestSnapshots,
  employmentForwardBannerCopy,
  isEmploymentForwardedUnderReview,
  readRequestSnapshotFromEvidence,
} from '../lib/employment';
import { mutationSeverityLabel } from '../lib/employment/employmentForwardCopy';
import { messageFromIdentityVerificationHttpError } from '../lib/identityVerificationHttpErrorMessage';
import { doctrineOutcomePlain, doctrineTransportPlain, summarizeLastActionResult } from '../lib/applicationJourneyCopy';
import {
  buildDecisionTabNarrative,
  buildIdentityTabNarrative,
  enrichIdentityNarrativeWithLinkage,
  buildIncomeTabNarrative,
} from '../lib/applicationTabNarratives';
import { buildPdlDecisionTabNarrative } from '../lib/pdlDecisionWhatsHappening';
import { resolvePdlDecisionStubPack } from '../dev/pdlDecisionEngineStubs';
import { WorkspaceFooter } from '../components/application/verificationTabUi';
import { TabWhatsHappeningBanner } from '../components/application/TabWhatsHappeningBanner';
import { ApplicationIncomeResultPanel } from '../components/income/ApplicationIncomeResultPanel';
import { ApplicationIncomeBankActivityPanel } from '../components/income/ApplicationIncomeBankActivityPanel';
import type { IncomeSubTarget } from '../lib/loanReview/mapLoanReviewToViewModel';
import { IncomeCalculationDerivationPanel } from '../components/income/IncomeCalculationDerivationPanel';
import { parseIncomeDepositPanelData } from '../lib/incomeDepositPanelData';
import { extractCalculationTransactionsFromDetail } from '../lib/incomeCalculationDerivation';
import {
  buildIncomeStreamAccentMap,
  parseIncludedIncomeStreamsFromDetail,
} from '../lib/incomeStreamPresentation';
import { adminDashboardBffApi } from '../api/admin';
import { ApplicationEmployerFootprintPanel } from '../components/application/ApplicationEmployerFootprintPanel';
import { ApplicationDeclaredEmploymentCard } from '../components/application/ApplicationDeclaredEmploymentCard';
import { ApplicationDeclaredIdentityCard } from '../components/application/ApplicationDeclaredIdentityCard';
import { EmployerReviewSummary } from '../components/employerReview/EmployerReviewSummary';
import { EmployerEvidenceTourProvider } from '../components/employerReview/guidedTour/EmployerEvidenceTourContext';
import { EvidenceGuideController } from '../components/employerReview/guidedTour/EvidenceGuideController';
import { WhatsHappeningReviewPathTour } from '../components/employerReview/guidedTour/WhatsHappeningReviewPathTour';
import { EmployerVerificationSections } from '../components/employerReview/EmployerVerificationSections';
import { mapEmployerVerificationToReviewSummary } from '../lib/employerReview/mapEmployerVerificationToReviewSummary';
import { deriveEmployerReviewPanelState } from '../lib/employerReview/employerReviewPanelState';
import { EmployerReviewPanelStatus } from '../components/application/EmployerReviewPanelStatus';
import { EmployerReviewSectionErrorBoundary } from '../components/application/EmployerReviewSectionErrorBoundary';
import { presentIdentityAttempts } from '../lib/identityAttemptPresentation';
function readListPayload(payload: unknown): { items: Record<string, unknown>[]; total?: number } {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { items: [] };
  }
  const o = payload as Record<string, unknown>;
  const raw = o.items;
  const items = Array.isArray(raw)
    ? raw.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x))
    : [];
  const total = typeof o.total === 'number' ? o.total : undefined;
  return { items, total };
}

function cell(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '—';
}

function errorsFor(
  scope: ApplicationVerificationSnapshotErrorDto['scope'],
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[],
): ApplicationVerificationSnapshotErrorDto[] {
  return snapshotErrors.filter((e) => e.scope === scope);
}

export function ApplicationIdentityTab({
  applicationId,
  snapshotErrors,
  application = null,
  onboarding = null,
  timeline = null,
}: {
  applicationId: string;
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[];
  application?: ApplicationJourneyHeaderDto | null;
  onboarding?: ApplicationOnboardingSignalsDto | null;
  timeline?: ApplicationTimelineEventDto[] | null;
}) {
  const scopeErrors = errorsFor('identity', snapshotErrors);
  const appId = applicationId.trim();

  const lookupQ = useQuery({
    queryKey: ['applicationIdentityLookupByApp', appId, application?.ccid ?? ''],
    queryFn: () =>
      resolveIdentityLookupForApplication(
        {
          applicationId: appId,
          ccid: application?.ccid,
        },
        identityVerificationDashboardApi.lookup,
      ),
    enabled: !!appId,
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const identityAttempts = useMemo(
    () => presentIdentityAttempts(lookupQ.data?.items ?? []),
    [lookupQ.data?.items],
  );
  const matchInsights = useMemo(
    () => buildIdentityMatchInsights(identityAttempts),
    [identityAttempts],
  );
  const postMatchInsights = useMemo(
    () =>
      buildIdentityPostMatchInsights({
        attempts: identityAttempts,
        matchInsights,
        application,
        onboarding,
        timeline,
      }),
    [identityAttempts, matchInsights, application, onboarding, timeline],
  );

  const hasLookupData = Boolean(lookupQ.data);
  const showIdentityBody = !lookupQ.isPending || hasLookupData;
  const mitekRows = useMemo(
    () => pickMitekVerificationRows(identityAttempts.map((a) => a.row)),
    [identityAttempts],
  );

  const identityNarrative = useMemo(
    () =>
      enrichIdentityNarrativeWithLinkage(
        buildIdentityTabNarrative(identityAttempts),
        application?.legacyLinkage,
      ),
    [identityAttempts, application?.legacyLinkage],
  );

  const showLegacyLinkage = application?.legacyLinkage != null;

  return (
    <Stack spacing={2.5}>
      {scopeErrors.map((e) => (
        <Alert key={e.message} severity="warning">
          Identity details are incomplete right now: {e.message}
        </Alert>
      ))}

      {/* Outside the lookup gate on purpose — an application with no identity
          session still has a profile worth correcting. */}
      <ApplicationDeclaredIdentityCard applicationId={appId} />

      {lookupQ.isPending && !hasLookupData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {lookupQ.isError ? (
        <Alert
          severity={hasLookupData ? 'warning' : 'error'}
          action={
            <Button color="inherit" size="small" onClick={() => void lookupQ.refetch()}>
              Retry
            </Button>
          }
        >
          {messageFromIdentityVerificationHttpError(lookupQ.error)}
          {hasLookupData
            ? ' Showing the last successful load — Retry once identity-verification is up.'
            : null}
        </Alert>
      ) : null}

      {showLegacyLinkage || hasLookupData ? (
        <TabWhatsHappeningBanner narrative={identityNarrative} />
      ) : null}

      {showLegacyLinkage ? (
        <ApplicationLegacyLinkagePanel
          application={application}
          decisionEnginePowerId={application?.decisionEngine?.powerId ?? null}
          identityLookupItem={lookupQ.data?.items?.[0] ?? null}
        />
      ) : null}

      {showIdentityBody && hasLookupData ? (
        <>
          {identityAttempts.length > 0 ? (
            <ApplicationSectionCard
              title="Verification attempts"
              subtitle="Complete attempt history for this application. Later re-verification does not replace earlier failures."
              icon={<HistoryRoundedIcon fontSize="small" />}
              data-testid="application-identity-attempt-history"
            >
              <Stack spacing={1.5}>
                {identityAttempts.map((attempt) => (
                  <ApplicationIdentityAttemptCard
                    key={attempt.row.iv_verification_id}
                    row={attempt.row}
                    policyTiers={[]}
                    isLatest={attempt.isLatest}
                    role={attempt.role}
                    roleLabel={attempt.roleLabel}
                    roleHint={attempt.roleHint}
                  />
                ))}
              </Stack>
            </ApplicationSectionCard>
          ) : null}
          {identityAttempts.length > 0 ? (
            <>
              <IdentityMatchInsightsGrid insights={matchInsights} />
              <IdentityPostMatchInsightsPanels insights={postMatchInsights} />
            </>
          ) : null}
          {mitekRows.map((row) => (
            <ApplicationMitekVerificationPanel key={`mitek-${row.iv_verification_id}`} row={row} />
          ))}
        </>
      ) : null}
      {lookupQ.data && lookupQ.data.items.length === 0 ? (
        <Alert severity="info">
          No identity verification sessions are linked to this application yet.
        </Alert>
      ) : null}
    </Stack>
  );
}

export function ApplicationIncomeTab({
  applicationId: _applicationId,
  data,
  snapshotErrors,
  lastActionResult,
  incomeJump,
}: {
  applicationId: string;
  data: unknown | null;
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[];
  lastActionResult?: Record<string, unknown> | null;
  /** Opens a specific Bank activity sub-view (e.g. the Gambling debits filter). */
  incomeJump?: { target: IncomeSubTarget; nonce: number } | null;
}) {
  const scopeErrors = errorsFor('income', snapshotErrors);
  const { items } = readListPayload(data);
  const lastActionFailSummary = summarizeLastActionResult(lastActionResult ?? undefined);
  const latest = items[0] ?? null;
  const priors = items.slice(1);
  const latestId = latest && typeof latest.id === 'string' ? latest.id : '';

  const detailQ = useQuery({
    queryKey: ['bffIncomeRecordDetail', latestId],
    queryFn: () => adminDashboardBffApi.incomeRecordDetail(latestId),
    enabled: !!latestId,
  });

  const depositData = useMemo(() => parseIncomeDepositPanelData(detailQ.data), [detailQ.data]);
  const calculationAudit = useMemo(
    () => extractCalculationTransactionsFromDetail(detailQ.data),
    [detailQ.data],
  );
  const includedIncomeStreams = useMemo(
    () => parseIncludedIncomeStreamsFromDetail(detailQ.data),
    [detailQ.data],
  );
  const incomeStreamAccentMap = useMemo(
    () => buildIncomeStreamAccentMap(includedIncomeStreams),
    [includedIncomeStreams],
  );
  const primaryPayor = useMemo(() => {
    const detailMetrics =
      detailQ.data &&
      typeof detailQ.data === 'object' &&
      !Array.isArray(detailQ.data) &&
      (detailQ.data as Record<string, unknown>).metrics &&
      typeof (detailQ.data as Record<string, unknown>).metrics === 'object'
        ? ((detailQ.data as Record<string, unknown>).metrics as Record<string, unknown>)
        : null;
    if (typeof detailMetrics?.primaryPayor === 'string' && detailMetrics.primaryPayor.trim()) {
      return detailMetrics.primaryPayor.trim();
    }
    const rowMetrics =
      latest?.metrics && typeof latest.metrics === 'object' && !Array.isArray(latest.metrics)
        ? (latest.metrics as Record<string, unknown>)
        : null;
    return typeof rowMetrics?.primaryPayor === 'string' ? rowMetrics.primaryPayor.trim() || null : null;
  }, [detailQ.data, latest]);

  return (
    <Stack spacing={2.5}>
      <TabWhatsHappeningBanner narrative={buildIncomeTabNarrative(data, lastActionFailSummary)} />
      {scopeErrors.map((e) => (
        <Alert key={e.message} severity="warning">
          Income details are incomplete right now: {e.message}
        </Alert>
      ))}

      {items.length === 0 ? (
        <Alert severity="info">No income verification results are on file for this application yet.</Alert>
      ) : latest ? (
        <>
          <ApplicationIncomeResultPanel row={latest} detail={detailQ.data} isLatest />
          {calculationAudit?.streams && calculationAudit.streams.length > 0 ? (
            <IncomeCalculationDerivationPanel
              audit={calculationAudit}
              primaryPayor={primaryPayor}
              includedStreams={includedIncomeStreams}
            />
          ) : null}
          <ApplicationIncomeBankActivityPanel
            isPending={detailQ.isPending}
            isError={detailQ.isError}
            error={detailQ.error}
            depositData={depositData}
            streamAccentMap={incomeStreamAccentMap}
            incomeJump={incomeJump}
          />
          {priors.length > 0 ? (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Prior attempts
              </Typography>
              {priors.map((row, index) => {
                const id = cell(row.id);
                return (
                  <ApplicationIncomeResultPanel
                    key={id !== '—' ? id : `income-prior-${index}`}
                    row={row}
                    isLatest={false}
                  />
                );
              })}
            </Stack>
          ) : null}
        </>
      ) : null}

      <WorkspaceFooter to="/income-verification" label="Open income verification workspace" />
    </Stack>
  );
}

/** Newest verification first; ties broken by id string. */
function employerSnapshotVerifiedAtMs(row: Record<string, unknown>): number {
  const v = row.verifiedAt;
  if (typeof v === 'string' && v.trim()) {
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

function sortEmployerSnapshotItemsNewestFirst(items: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...items].sort((a, b) => {
    const tb = employerSnapshotVerifiedAtMs(b);
    const ta = employerSnapshotVerifiedAtMs(a);
    if (tb !== ta) return tb - ta;
    const ida = a.id != null ? String(a.id) : '';
    const idb = b.id != null ? String(b.id) : '';
    return idb.localeCompare(ida);
  });
}

const EMPLOYER_VERIFICATION_DIFF_ANCHOR_ID = 'employer-verification-attempt-diff';

function formatEmployerHistoryAt(iso: string | null | undefined): string {
  if (iso == null || typeof iso !== 'string' || !iso.trim()) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const md = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d);
  const hm = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: false }).format(d);
  return `${md} · ${hm}`;
}

function readEmployerRowGates(row: Record<string, unknown>): Record<string, string | null> {
  const gatesRaw = row.gates;
  if (!gatesRaw || typeof gatesRaw !== 'object' || Array.isArray(gatesRaw)) return {};
  return gatesRaw as Record<string, string | null>;
}

function rowConfidenceBucket(row: Record<string, unknown>): string | null {
  const confidenceRaw =
    typeof row.confidenceScore === 'number' && Number.isFinite(row.confidenceScore)
      ? row.confidenceScore
      : null;
  return confidenceRaw != null ? confidenceBucketLabel(normalizedConfidenceScore(confidenceRaw)) : null;
}

function normGateRaw(v: string | null | undefined): string {
  return (typeof v === 'string' ? v : '').trim().toUpperCase();
}

/** `newerRun` is the more recent attempt (lower index in newest-first lists). */
function employerAttemptPairAnalysis(
  newerRun: Record<string, unknown>,
  olderRun: Record<string, unknown>,
): {
  overallUnchanged: boolean;
  changedGates: GateKey[];
  firstChangedGateTitle: string | null;
} {
  const gN = readEmployerRowGates(newerRun);
  const gO = readEmployerRowGates(olderRun);
  const changedGates: GateKey[] = [];
  for (const gate of GATE_ORDER_PIPE) {
    if (normGateRaw(gN[gate]) !== normGateRaw(gO[gate])) changedGates.push(gate);
  }
  const fN = typeof newerRun.finalStatus === 'string' ? newerRun.finalStatus : '';
  const fO = typeof olderRun.finalStatus === 'string' ? olderRun.finalStatus : '';
  const cN = rowConfidenceBucket(newerRun);
  const cO = rowConfidenceBucket(olderRun);
  const overallUnchanged = changedGates.length === 0 && fN === fO && cN === cO;
  return {
    overallUnchanged,
    changedGates,
    firstChangedGateTitle: changedGates.length ? `${gateMeta[changedGates[0]].title} updated` : null,
  };
}

function employerHistoryOutcomeLine(row: Record<string, unknown>): string {
  const gates = readEmployerRowGates(row);
  const storedFinal = typeof row.finalStatus === 'string' ? row.finalStatus : null;
  const gateKey = firstAttentionGateKey(gates);
  if (gateKey) {
    const raw = gates[gateKey];
    const u = (typeof raw === 'string' ? raw : '').trim().toUpperCase();
    const action =
      u === 'FAIL' ? 'failed' : u === 'REVIEW' ? 'needs review' : gateStatusShort(raw).toLowerCase();
    return `${gateMeta[gateKey].title} ${action}`;
  }
  return finalOutcomeLabelStored(storedFinal);
}

function readFailReasonPreview(row: Record<string, unknown>): string | null {
  const hp = row.hoverPayload;
  if (!hp || typeof hp !== 'object' || Array.isArray(hp)) return null;
  const v = (hp as Record<string, unknown>).failReasonPreview;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function employerDecisionAlertSeverity(storedFinal: string | null): AlertColor {
  const u = (storedFinal ?? '').trim().toUpperCase();
  if (u === 'VERIFIED') return 'success';
  if (u === 'REJECTED') return 'error';
  if (u === 'MANUAL_REVIEW') return 'warning';
  return 'info';
}

function reasonSummaryOneLine(full: string, maxLen = 140): string {
  const t = full.trim();
  if (!t) return '';
  const idx = t.indexOf('. ');
  if (idx > 0 && idx + 1 <= maxLen) return t.slice(0, idx + 1);
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

function pickOperatorFacingReason(full: string | null | undefined): string | null {
  const t = full?.trim();
  if (!t) return null;
  const lines = t
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const hit = lines.find((l) => / — FAIL:/i.test(l) || / — REVIEW:/i.test(l));
  if (hit) return hit;
  return t;
}

function formatEmployerLegalNameDisplay(raw: string): string {
  if (raw === '—' || !raw.trim()) return raw;
  const capToken = (token: string) => {
    if (!token) return token;
    const lower = token.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };
  return raw
    .split(/\s+/)
    .map((word) => word.split('-').map(capToken).join('-'))
    .join(' ');
}

function summaryStatusRailLabel(storedFinal: string | null): string {
  return finalOutcomeLabelStored(storedFinal);
}

function summarySeverityInsight(storedFinal: string | null, confidenceBucket: string | null): string | null {
  const u = (storedFinal ?? '').trim().toUpperCase();
  const b = confidenceBucket ?? '';
  if (b === 'Low') {
    return 'Some employer records could not be verified automatically.';
  }
  if (b === 'Medium' && (u === 'REJECTED' || u === 'MANUAL_REVIEW')) {
    return null;
  }
  if (u === 'REJECTED' || u === 'MANUAL_REVIEW') {
    return 'Manual review recommended.';
  }
  return null;
}

function summaryOutcomeStatusIcon(severity: AlertColor) {
  const iconSx = { fontSize: 18, flexShrink: 0 } as const;
  switch (severity) {
    case 'success':
      return <CheckCircleOutlineRoundedIcon sx={{ ...iconSx, color: 'success.main' }} />;
    case 'error':
      return <WarningAmberRoundedIcon sx={{ ...iconSx, color: 'error.main' }} />;
    case 'warning':
      return <WarningAmberRoundedIcon sx={{ ...iconSx, color: 'warning.main' }} />;
    default:
      return <InfoOutlinedIcon sx={{ ...iconSx, color: 'info.main' }} />;
  }
}

function EmployerSnapshotSummaryCard({
  row,
  applicationId,
  attemptWrapperId,
  showLatestBadge,
}: {
  row: Record<string, unknown>;
  applicationId: string;
  attemptWrapperId?: string;
  showLatestBadge?: boolean;
}) {
  const navigate = useNavigate();
  const employer = row.employer as Record<string, unknown> | undefined;
  const legalRaw = employer && typeof employer.legalName === 'string' ? employer.legalName : '—';
  const legalDisplay = formatEmployerLegalNameDisplay(legalRaw);
  const storedFinal = typeof row.finalStatus === 'string' ? row.finalStatus : null;
  const gatesRaw = row.gates;
  const gates =
    gatesRaw && typeof gatesRaw === 'object' && !Array.isArray(gatesRaw)
      ? (gatesRaw as Record<string, string | null>)
      : {};
  const failReason =
    typeof row.failReason === 'string' && row.failReason.trim() ? row.failReason.trim() : null;
  const reasonRaw = failReason ?? readFailReasonPreview(row);
  const reasonFocus = pickOperatorFacingReason(reasonRaw);
  const summaryHeadline = summaryHeadlineFromFailReason({ reasonFocus, reasonRaw });
  const outcomeNarrative = summaryHeadline ? { headline: summaryHeadline } : null;
  const confidenceRaw =
    typeof row.confidenceScore === 'number' && Number.isFinite(row.confidenceScore)
      ? row.confidenceScore
      : null;
  const confidenceBucket =
    confidenceRaw != null ? confidenceBucketLabel(normalizedConfidenceScore(confidenceRaw)) : null;
  const decisionSeverity = employerDecisionAlertSeverity(storedFinal);
  const nextStep = recommendedNextStepCopy(storedFinal);
  const nextBullets = nextStep ? splitRecommendedActionsForDrawer(nextStep) : [];
  const isRejected = (storedFinal ?? '').trim().toUpperCase() === 'REJECTED';
  const failureReasonBody =
    reasonFocus != null && reasonFocus.trim()
      ? reasonSummaryOneLine(reasonFocus, 220)
      : reasonRaw != null && reasonRaw.trim()
        ? reasonSummaryOneLine(reasonRaw, 220)
        : null;
  const verifiedIso = row.verifiedAt != null && row.verifiedAt !== '' ? String(row.verifiedAt) : '';
  const verifiedRail = verifiedIso
    ? formatVerifiedRelativeRail(verifiedIso) || cell(row.verifiedAt)
    : '';
  const severityLine = summarySeverityInsight(storedFinal, confidenceBucket);
  const severityLineDisplay = isRejected ? null : severityLine;
  const passedGateTitles = GATE_ORDER_PIPE.filter((gate) => {
    const v = gates[gate];
    const raw = typeof v === 'string' ? v : null;
    return gateStatusChip(raw).color === 'success';
  }).map((gate) => gateMeta[gate].title);

  const statusMetaLine = [confidenceBucket ? `${confidenceBucket} confidence` : '', verifiedRail].filter(Boolean).join(' · ');

  return (
    <Box
      id={attemptWrapperId}
      sx={{
        mb: 0.5,
        py: 0.75,
        px: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        component="div"
        elevation={0}
        sx={(theme) => {
          const sev = decisionSeverity;
          const key = sev === 'error' || sev === 'success' || sev === 'warning' || sev === 'info' ? sev : 'info';
          const accent = theme.palette[key].main;
          const neutralBorder = alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.14 : 0.06);
          const lift = alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.06);
          return {
            p: { xs: 1.75, sm: 2.5 },
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${neutralBorder}`,
            borderLeft: `3px solid ${accent}`,
            boxShadow: `0 1px 2px ${alpha('#101820', 0.04)}, 0 1px 3px ${alpha('#101820', 0.06)}`,
            transition: 'box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease',
            '&:hover': {
              boxShadow: `0 2px 6px ${alpha('#101820', 0.06)}, 0 4px 14px ${lift}`,
              transform: 'translateY(-1px)',
              borderColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.09),
            },
          };
        }}
      >
        <Stack spacing={0} sx={{ width: '100%' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems="flex-start"
            columnGap={{ md: 3 }}
            rowGap={1}
            sx={{ mb: 0 }}
          >
            <Box sx={{ flex: { md: '1 1 0' }, minWidth: 0, pr: { md: 3 } }}>
              <Stack spacing={0.35}>
                {showLatestBadge ? (
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '0.14em',
                      color: 'primary.main',
                      lineHeight: 1.2,
                    }}
                  >
                    LATEST
                  </Typography>
                ) : null}
                <Typography
                  component="h3"
                  sx={{
                    m: 0,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 600,
                    lineHeight: 1.25,
                    letterSpacing: '-0.03em',
                    color: 'text.primary',
                  }}
                >
                  {legalDisplay}
                </Typography>
                {severityLineDisplay ? (
                  <Typography
                    sx={{
                      m: 0,
                      fontSize: '0.8125rem',
                      lineHeight: 1.45,
                      color: 'text.secondary',
                    }}
                  >
                    {severityLineDisplay}
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            <Stack
              spacing={1.25}
              alignItems="flex-end"
              sx={(theme) => ({
                alignSelf: 'flex-start',
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 140 },
                maxWidth: { md: 280 },
                textAlign: 'right',
                borderLeft: { xs: 'none', md: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.45) : '#f1f3f5'}` },
                pl: { md: 3 },
                ml: { md: 3 },
                pt: { xs: 0.25, md: 0 },
              })}
            >
              <Tooltip title={`Stored: ${finalOutcomeTechnical(storedFinal)}`} placement="top" arrow>
                <Box component="span" sx={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={summaryOutcomeStatusIcon(decisionSeverity)}
                    label={summaryStatusRailLabel(storedFinal)}
                    color={employerVerificationFinalChipColor(storedFinal)}
                    sx={{
                      fontWeight: 700,
                      height: 28,
                      fontSize: '0.8125rem',
                      '& .MuiChip-icon': { ml: 0.5 },
                      transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
                      '&:hover': {
                        boxShadow: (theme) => `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
                      },
                    }}
                  />
                </Box>
              </Tooltip>

              {statusMetaLine ? (
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                    fontWeight: 500,
                    color: 'text.secondary',
                    maxWidth: 280,
                  }}
                >
                  {statusMetaLine}
                </Typography>
              ) : null}
            </Stack>
          </Stack>

          <Box sx={{ maxWidth: 720, mt: 0.75 }}>
            {outcomeNarrative ? (
              <Tooltip title={reasonFocus ?? reasonRaw ?? ''} placement="top" arrow>
                <Stack
                  spacing={0.35}
                  sx={{
                    mb:
                      passedGateTitles.length > 0 || (!isRejected && nextBullets.length > 0) || (isRejected && failureReasonBody)
                        ? 1
                        : 0,
                  }}
                >
                  <Typography
                    sx={{
                      m: 0,
                      fontSize: isRejected ? '1rem' : '1.0625rem',
                      fontWeight: 600,
                      lineHeight: 1.35,
                      letterSpacing: '-0.02em',
                      color: 'text.primary',
                    }}
                  >
                    {outcomeNarrative.headline}
                  </Typography>
                </Stack>
              </Tooltip>
            ) : null}

            {passedGateTitles.length > 0 ? (
              <Stack spacing={0.35} sx={{ mb: !isRejected && nextBullets.length > 0 ? 1.25 : 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', color: 'text.secondary' }}>
                  Verified checks
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, color: 'text.secondary' }}>
                  {passedGateTitles.map((t) => `✓ ${t}`).join(' · ')}
                </Typography>
              </Stack>
            ) : null}

            {isRejected && failureReasonBody ? (
              <Stack spacing={0.35} sx={{ mt: 1.25 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  Failure reason
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45, color: 'text.primary' }}>
                  {failureReasonBody}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  Hard stop applied
                </Typography>
              </Stack>
            ) : !isRejected && nextBullets.length > 0 ? (
              <Stack spacing={0.35} sx={{ mt: 1.5 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    display: 'block',
                    fontSize: '0.75rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  Recommended actions
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 1.35,
                    color: 'text.secondary',
                    fontSize: '0.8125rem',
                    listStylePosition: 'outside',
                  }}
                >
                  {nextBullets.map((line, i) => (
                    <Typography
                      key={i}
                      component="li"
                      sx={{
                        py: 0,
                        lineHeight: 1.4,
                        display: 'list-item',
                        color: 'text.secondary',
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            ) : null}
            <Box sx={{ mt: 1.25 }}>
              <GateOutcomePickerChips
                gates={gates}
                onSelect={(gate) =>
                  navigate(employerInvestigationHref(cell(row.id), { applicationId, gate }))
                }
              />
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

function employerInvestigationHref(
  verificationId: string,
  opts?: { applicationId?: string; gate?: GateKey },
): string {
  const sp = new URLSearchParams();
  if (opts?.applicationId) sp.set('applicationId', opts.applicationId);
  if (opts?.gate) sp.set('gate', opts.gate);
  const q = sp.toString();
  return `/employer-verification/investigation/${encodeURIComponent(verificationId)}${q ? `?${q}` : ''}`;
}

const CONFIDENCE_RANK: Record<string, number> = { Low: 0, Medium: 1, High: 2 };

function EmployerVerificationAttemptComparison({
  latest,
  previous,
  totalAttempts,
}: {
  latest: Record<string, unknown>;
  previous: Record<string, unknown>;
  totalAttempts: number;
}) {
  const [noChangeExpanded, setNoChangeExpanded] = useState(false);
  const gLatest = readEmployerRowGates(latest);
  const gPrev = readEmployerRowGates(previous);
  const gateDiffLines = GATE_ORDER_PIPE.map((gate) => {
    const a = gateStatusShort(gPrev[gate] ?? null);
    const b = gateStatusShort(gLatest[gate] ?? null);
    return { gate, text: `${gateMeta[gate].title}: ${a} → ${b}`, unchanged: a === b };
  });
  const fLatest = typeof latest.finalStatus === 'string' ? latest.finalStatus : null;
  const fPrev = typeof previous.finalStatus === 'string' ? previous.finalStatus : null;
  const cLatest = rowConfidenceBucket(latest);
  const cPrev = rowConfidenceBucket(previous);
  const outcomeLine = `Outcome: ${finalOutcomeLabelStored(fPrev)} → ${finalOutcomeLabelStored(fLatest)}`;
  const gatesUnchanged = gateDiffLines.every((l) => l.unchanged);
  const finalUnchanged = (fPrev ?? '') === (fLatest ?? '');
  const confUnchanged = cPrev === cLatest;
  const snapshotDiffLines = diffRequestSnapshots(
    readRequestSnapshotFromEvidence(previous.evidence),
    readRequestSnapshotFromEvidence(latest.evidence),
  );
  const snapshotsUnchanged = snapshotDiffLines.length === 0;
  const overallUnchanged = gatesUnchanged && finalUnchanged && confUnchanged && snapshotsUnchanged;

  const changedBullets: string[] = [];
  if (!finalUnchanged) changedBullets.push(outcomeLine);
  if (!confUnchanged && cPrev && cLatest) {
    const rL = CONFIDENCE_RANK[cLatest] ?? 0;
    const rP = CONFIDENCE_RANK[cPrev] ?? 0;
    if (rL > rP) changedBullets.push(`✓ Confidence increased (${cPrev} → ${cLatest})`);
    else if (rL < rP) changedBullets.push(`✕ Confidence decreased (${cPrev} → ${cLatest})`);
    else changedBullets.push(`Confidence: ${cPrev} → ${cLatest}`);
  }

  return (
    <Box
      id={EMPLOYER_VERIFICATION_DIFF_ANCHOR_ID}
      sx={{
        mt: 2,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Attempt comparison
      </Typography>
      {totalAttempts > 2 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Latest vs prior run ({totalAttempts} attempts total).
        </Typography>
      ) : null}

      {overallUnchanged ? (
        <Stack spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            No changes detected
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setNoChangeExpanded((v) => !v)}
            sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          >
            {noChangeExpanded ? 'Hide comparison ↑' : 'View comparison ↓'}
          </Button>
          {noChangeExpanded ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 520 }}>
              Compared latest run to the prior run. Gates, confidence band, and stored outcome all matched.
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          {changedBullets.length > 0 ? (
            <Stack spacing={0.35}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', color: 'text.secondary' }}>
                Changed signals
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, pl: 1.35, color: 'text.secondary', fontSize: '0.8125rem', listStylePosition: 'outside' }}
              >
                {changedBullets.map((line, i) => (
                  <Typography key={i} component="li" sx={{ lineHeight: 1.45, display: 'list-item' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Stack>
          ) : null}
          <Stack spacing={0.35}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', color: 'text.secondary' }}>
              Gate outcomes
            </Typography>
            {gateDiffLines.map((l) => (
              <Typography key={l.gate} variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, color: 'text.secondary' }}>
                {l.text}
              </Typography>
            ))}
          </Stack>
          {snapshotDiffLines.length > 0 ? (
            <Stack spacing={0.35} data-testid="employment-attempt-field-diff">
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', color: 'text.secondary' }}>
                Submitted field changes
              </Typography>
              {snapshotDiffLines.map((line) => (
                <Typography key={line.field} variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
                  {line.label}: {line.previous ?? '—'} → {line.current ?? '—'}
                  {line.mutationSeverity !== 'none' ? ` (${mutationSeverityLabel(line.mutationSeverity)})` : ''}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}

function EmployerVerificationHistoryList({
  rows,
  applicationId,
}: {
  rows: Record<string, unknown>[];
  applicationId: string;
}) {
  const navigate = useNavigate();

  return (
    <Stack spacing={0} sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
        Attempt history
      </Typography>
      <Stack spacing={1.75}>
        {rows.map((row, index) => {
          const rowKey = row.id != null ? String(row.id) : `idx-${index}`;
          const id = cell(row.id);
          const gates = readEmployerRowGates(row);
          const gateDefault = firstAttentionGateKey(gates) ?? undefined;
          const isLatest = index === 0;
          const verifiedIso = row.verifiedAt != null && row.verifiedAt !== '' ? String(row.verifiedAt) : '';
          const when = formatEmployerHistoryAt(verifiedIso);
          const outcome = employerHistoryOutcomeLine(row);
          const bucket = rowConfidenceBucket(row);
          const unchangedVsNewer =
            index > 0 ? employerAttemptPairAnalysis(rows[index - 1]!, rows[index]!).overallUnchanged : false;

          const openInvestigation = (gate: GateKey) => {
            navigate(employerInvestigationHref(id, { applicationId, gate }));
          };

          const openDefaultInvestigation = () => {
            navigate(employerInvestigationHref(id, { applicationId, gate: gateDefault }));
          };

          return (
            <Box
              key={rowKey}
              sx={{
                pl: 1.5,
                borderLeft: '2px solid',
                borderColor: isLatest ? 'primary.main' : 'divider',
                opacity: isLatest ? 1 : 0.55,
                transform: isLatest ? 'none' : 'scale(0.97)',
                transformOrigin: 'top left',
              }}
            >
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                {isLatest ? (
                  <>
                    <Typography
                      variant="overline"
                      sx={{ fontWeight: 800, letterSpacing: '0.14em', color: 'primary.main', lineHeight: 1.2 }}
                    >
                      LATEST
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                      {when}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45, color: 'text.primary' }}>
                      {outcome}
                    </Typography>
                    {bucket ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {bucket} confidence
                      </Typography>
                    ) : null}
                    <Button
                      variant="text"
                      size="small"
                      onClick={openDefaultInvestigation}
                      sx={{
                        alignSelf: 'flex-start',
                        px: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                      }}
                    >
                      Review →
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Previous · {when}
                    </Typography>
                    {unchangedVsNewer ? (
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                        No changes
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, color: 'text.secondary' }}>
                        {[outcome, bucket ? `${bucket} confidence` : null].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    <Box sx={{ pt: 0.35 }}>
                      <GateOutcomePickerChips gates={gates} onSelect={openInvestigation} />
                    </Box>
                    <Button
                      variant="text"
                      size="small"
                      onClick={openDefaultInvestigation}
                      sx={{
                        alignSelf: 'flex-start',
                        px: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        color: 'text.secondary',
                      }}
                    >
                      View details →
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

export function ApplicationEmployerTab({
  applicationId,
  application,
  data,
  snapshotErrors,
}: {
  applicationId: string;
  application?: ApplicationJourneyHeaderDto;
  data: unknown | null;
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[];
}) {
  const scopeErrors = errorsFor('employer', snapshotErrors);
  const { items: snapshotItems } = readListPayload(data);

  const directListQ = useQuery({
    queryKey: ['employerVerificationListByApplication', applicationId],
    queryFn: () =>
      employerVerificationDashboardApi.list({
        applicationIdContains: applicationId,
        limit: 50,
        offset: 0,
        latestPerApplicationOnly: false,
      }),
    enabled: !!applicationId.trim(),
  });

  const footprintQ = useQuery({
    queryKey: ['employerFootprintAdmin', applicationId],
    queryFn: () => employerVerificationDashboardApi.employerFootprint(applicationId),
    enabled: !!applicationId.trim(),
    staleTime: 30_000,
    refetchInterval: (q) => {
      const s = String(q.state.data?.status ?? '').toUpperCase();
      return s === 'PENDING' || s === 'RUNNING' ? 5_000 : false;
    },
  });

  const sortedItems = useMemo(() => {
    const base =
      snapshotItems.length > 0 ? snapshotItems : ((directListQ.data?.items ?? []) as Record<string, unknown>[]);
    return sortEmployerSnapshotItemsNewestFirst(base);
  }, [snapshotItems, directListQ.data?.items]);

  const usedDirectList = snapshotItems.length === 0 && (directListQ.data?.items?.length ?? 0) > 0;

  const attemptCount = sortedItems.length;
  const latestRow = sortedItems[0];
  const latestVerificationId =
    latestRow && typeof latestRow.id === 'string' && latestRow.id.trim() ? latestRow.id.trim() : null;

  const latestDetailQ = useQuery({
    queryKey: ['employerVerificationDetail', latestVerificationId],
    queryFn: () => employerVerificationDashboardApi.detail(latestVerificationId!),
    enabled: !!latestVerificationId,
    staleTime: 60_000,
  });

  const dossierQ = useQuery({
    queryKey: ['companyDossier', latestVerificationId],
    queryFn: () => employerVerificationDashboardApi.companyDossier(latestVerificationId!),
    enabled: !!latestVerificationId,
    staleTime: 60_000,
  });

  const pairDelta =
    attemptCount >= 2 ? employerAttemptPairAnalysis(sortedItems[0]!, sortedItems[1]!) : null;
  const showForwardBanner =
    application &&
    isEmploymentForwardedUnderReview({
      milestones: application.completedMilestones,
      lastActionResult: application.lastActionResult,
    });

  const employerName =
    latestRow?.employer && typeof latestRow.employer === 'object' && !Array.isArray(latestRow.employer)
      ? String((latestRow.employer as { legalName?: unknown }).legalName ?? '').trim() || null
      : null;

  const reviewSummary = useMemo(
    () =>
      mapEmployerVerificationToReviewSummary({
        detail: latestDetailQ.data ?? null,
        finalStatus: latestRow ? String(latestRow.finalStatus ?? latestRow.status ?? '') : null,
        declaredEmployer: employerName,
        footprint: footprintQ.data ?? null,
        application,
        hasRecords: sortedItems.length > 0,
      }),
    [
      latestDetailQ.data,
      latestRow,
      employerName,
      footprintQ.data,
      application,
      sortedItems.length,
    ],
  );

  const footprintActive =
    footprintQ.data != null && String(footprintQ.data.status ?? '').toUpperCase() !== 'NOT_STARTED';

  const declaredEmployerAddress = useMemo(() => {
    const fromFootprint = (footprintQ.data?.declaredEmployerAddress ?? '').trim();
    if (fromFootprint) return fromFootprint;
    const fromDetail = readRequestSnapshotFromEvidence(latestDetailQ.data?.evidence)
      ?.employerAddressNormalized?.trim();
    if (fromDetail) return fromDetail;
    const fromDossier = dossierQ.data?.requestSnapshot?.employerAddressNormalized?.trim();
    if (fromDossier) return fromDossier;
    return null;
  }, [
    footprintQ.data?.declaredEmployerAddress,
    latestDetailQ.data?.evidence,
    dossierQ.data?.requestSnapshot?.employerAddressNormalized,
  ]);

  const panelState = useMemo(
    () =>
      deriveEmployerReviewPanelState({
        snapshotItemsCount: snapshotItems.length,
        scopeErrors,
        directListQ: {
          isPending: directListQ.isPending,
          isFetching: directListQ.isFetching,
          isError: directListQ.isError,
          isSuccess: directListQ.isSuccess,
          error: directListQ.error,
        },
        footprintQ: {
          isPending: footprintQ.isPending,
          isFetching: footprintQ.isFetching,
          isError: footprintQ.isError,
          isSuccess: footprintQ.isSuccess,
          error: footprintQ.error,
        },
        latestDetailQ: {
          isPending: latestDetailQ.isPending,
          isFetching: latestDetailQ.isFetching,
          isError: latestDetailQ.isError,
          isSuccess: latestDetailQ.isSuccess,
          error: latestDetailQ.error,
        },
        sortedItemsCount: sortedItems.length,
        hasLatestVerificationId: !!latestVerificationId,
        usedDirectList,
      }),
    [
      snapshotItems.length,
      scopeErrors,
      directListQ.isPending,
      directListQ.isFetching,
      directListQ.isError,
      directListQ.isSuccess,
      directListQ.error,
      footprintQ.isPending,
      footprintQ.isFetching,
      footprintQ.isError,
      footprintQ.isSuccess,
      footprintQ.error,
      latestDetailQ.isPending,
      latestDetailQ.isFetching,
      latestDetailQ.isError,
      latestDetailQ.isSuccess,
      latestDetailQ.error,
      sortedItems.length,
      latestVerificationId,
      usedDirectList,
    ],
  );

  const retryingEmployerReview =
    directListQ.isFetching || footprintQ.isFetching || latestDetailQ.isFetching;

  const handleRetryEmployerReview = () => {
    void directListQ.refetch();
    void footprintQ.refetch();
    if (latestVerificationId) {
      void latestDetailQ.refetch();
      void dossierQ.refetch();
    }
  };

  const devEmployerDiagnostics = import.meta.env.DEV
    ? [
        directListQ.isError && directListQ.error instanceof Error
          ? `directList: ${directListQ.error.message}`
          : null,
        footprintQ.isError && footprintQ.error instanceof Error
          ? `footprint: ${footprintQ.error.message}`
          : null,
        latestDetailQ.isError && latestDetailQ.error instanceof Error
          ? `detail: ${latestDetailQ.error.message}`
          : null,
        scopeErrors[0]?.message ? `snapshot: ${scopeErrors[0]!.message}` : null,
      ].filter((line): line is string => !!line)
    : undefined;

  const showFootprintPanel =
    footprintQ.isSuccess &&
    footprintQ.data != null &&
    footprintActive &&
    !footprintQ.isError;

  const detailPending =
    Boolean(latestVerificationId) && latestDetailQ.isPending && latestDetailQ.data == null;

  const prioritizeGateAnalysis = reviewSummary.issueCount > 0;

  const verificationGatesSection = panelState.canShowVerificationContent ? (
    <EmployerReviewSectionErrorBoundary
      sectionLabel="Verification gates"
      onRetry={handleRetryEmployerReview}
    >
      {!latestDetailQ.isPending && !latestDetailQ.data && !latestDetailQ.isError ? (
        <Paper variant="outlined" sx={{ overflow: 'auto', p: 1.75, borderRadius: 2 }}>
          <EmployerSnapshotSummaryCard
            row={sortedItems[0]}
            applicationId={applicationId}
            attemptWrapperId="employer-verification-latest"
            showLatestBadge
          />
        </Paper>
      ) : null}
      <EmployerVerificationSections
        section="gates"
        reviewSummary={reviewSummary}
        detail={latestDetailQ.data ?? null}
        footprint={footprintQ.data ?? null}
        dossier={dossierQ.data}
        pairDelta={pairDelta}
        detailLoading={latestDetailQ.isPending && !latestDetailQ.isError}
      />

      {attemptCount >= 2 ? (
        <Stack spacing={1.25}>
          <EmployerVerificationAttemptComparison
            latest={sortedItems[0]!}
            previous={sortedItems[1]!}
            totalAttempts={sortedItems.length}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Prior attempts
          </Typography>
          {sortedItems.slice(1).map((priorRow, index) => (
            <EmployerAttemptDetailAccordion
              key={priorRow.id != null ? String(priorRow.id) : `prior-${index}`}
              row={priorRow}
              defaultExpanded={false}
            />
          ))}
          <EmployerVerificationHistoryList rows={sortedItems} applicationId={applicationId} />
        </Stack>
      ) : null}
    </EmployerReviewSectionErrorBoundary>
  ) : null;

  return (
    <EmployerEvidenceTourProvider
      findings={reviewSummary.evidenceFindings}
      issueCount={reviewSummary.issueCount}
    >
    <WhatsHappeningReviewPathTour
      findings={reviewSummary.evidenceFindings}
      issueCount={reviewSummary.issueCount}
    />
    <EvidenceGuideController />
    <Stack spacing={2.5} data-tour-target="employer-review-panel">
      <EmployerReviewPanelStatus
        state={panelState}
        onRetry={panelState.retryable ? handleRetryEmployerReview : undefined}
        retrying={retryingEmployerReview}
        devDiagnostics={devEmployerDiagnostics}
      />
      {panelState.showInitialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }} aria-busy="true">
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {panelState.canShowVerificationContent ? (
        <EmployerReviewSectionErrorBoundary
          sectionLabel="Employer review summary"
          onRetry={handleRetryEmployerReview}
        >
          {detailPending ? (
            <Stack spacing={1.5} aria-busy="true" data-testid="employer-review-detail-pending">
              <Skeleton variant="rounded" height={128} />
              <Skeleton variant="rounded" height={220} />
            </Stack>
          ) : (
            <>
              <EmployerReviewSummary
                vm={reviewSummary}
                detail={latestDetailQ.data ?? null}
                footprint={footprintQ.data ?? null}
              />
              <EmployerReviewSectionErrorBoundary
                sectionLabel="Verification evidence"
                onRetry={handleRetryEmployerReview}
              >
                <EmployerVerificationSections
                  section="brief"
                  reviewSummary={reviewSummary}
                  detail={latestDetailQ.data ?? null}
                  footprint={footprintQ.data ?? null}
                  dossier={dossierQ.data}
                  pairDelta={pairDelta}
                  detailLoading={latestDetailQ.isPending && !latestDetailQ.isError}
                />
              </EmployerReviewSectionErrorBoundary>
            </>
          )}
        </EmployerReviewSectionErrorBoundary>
      ) : null}
      <ApplicationDeclaredEmploymentCard applicationId={applicationId} />
      {showForwardBanner ? (
        <Alert
          severity="info"
          variant="outlined"
          data-testid="employment-forward-banner"
          sx={{
            color: 'text.primary',
            '& .MuiAlert-icon': { color: 'info.dark', opacity: 0.95 },
            '& .MuiAlert-message': { color: 'text.primary', fontWeight: 500 },
          }}
        >
          {employmentForwardBannerCopy({ lastActionResult: application?.lastActionResult })}
        </Alert>
      ) : null}
      {prioritizeGateAnalysis ? verificationGatesSection : null}
      {showFootprintPanel ? (
        <EmployerReviewSectionErrorBoundary
          sectionLabel="Digital footprint"
          onRetry={handleRetryEmployerReview}
        >
          <ApplicationEmployerFootprintPanel
            view={footprintQ.data!}
            declaredEmployerAddress={declaredEmployerAddress}
            subdued={reviewSummary.issueCount > 0}
          />
        </EmployerReviewSectionErrorBoundary>
      ) : null}
      {panelState.canShowEmptyState ? (
        <Alert severity="info">
          {application?.status?.toUpperCase() === 'DECLINED'
            ? `No employer verification records — this application was declined${
                application.declineReason
                  ? ` (${application.declineReason.replace(/_/g, ' ')})`
                  : ''
              } before employment verification ran.`
            : footprintActive
              ? 'Formal employer verification has not started yet — digital footprint is shown above.'
              : !application?.completedMilestones?.includes('INCOME_VERIFIED')
                ? 'No employer verification records yet — income verification has not completed, so employment has not started.'
                : 'No employer verification records found for this application.'}
        </Alert>
      ) : null}
      {!prioritizeGateAnalysis ? verificationGatesSection : null}
    </Stack>
    </EmployerEvidenceTourProvider>
  );
}

export function ApplicationDecisionTab({
  status,
  executions,
  decisionEngine,
  incomeRecords,
  applicationStatus,
  declineReason,
  application,
  deviceRisk,
  onNavigate,
}: {
  applicationId: string;
  status: string;
  executions: ApplicationJourneyExecutionDto[];
  loanReview?: ApplicationJourneyHeaderDto['loanReview'];
  decisionEngine?: ApplicationJourneyHeaderDto['decisionEngine'];
  incomeRecords?: unknown | null;
  applicationStatus?: string;
  declineReason?: string | null;
  application?: ApplicationJourneyHeaderDto;
  deviceRisk?: ApplicationDeviceRiskResponseDto | null;
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  // Decision engine tab always shows automated underwriting (DE panel). The manual
  // review cockpit is on the Manual review tab — see ApplicationDetailPage tab 7.
  const decidedRecord =
    application && shouldShowEmployeeDecisionRecordOnDecisionTab(application.status ?? status) ? (
      <ApplicationEmployeeDecisionPanel application={application} deviceRisk={deviceRisk} onNavigate={onNavigate} />
    ) : null;

  const pdlStub =
    import.meta.env.DEV ? resolvePdlDecisionStubPack(import.meta.env.VITE_PDL_DECISION_STUB) : null;
  const decisionEngineForTab = pdlStub
    ? { ...decisionEngine, ...pdlStub.decisionEngine }
    : decisionEngine;
  const declineReasonForTab = pdlStub?.applicationDeclineReason ?? declineReason;

  const isPdl = isPdlLoanType({
    productCode: decisionEngineForTab?.productCode,
    productType: application?.productType,
  });
  const pdlBriefActive =
    isPdl &&
    Boolean(
      decisionEngineForTab?.evaluationId ||
        decisionEngineForTab?.decision ||
        declineReasonForTab,
    );

  const narrative = buildDecisionTabNarrative({
    decisionEngine: decisionEngineForTab,
    declineReason: declineReasonForTab,
    applicationStatus,
    executions,
  });
  const whatsHappeningNarrative =
    isPdl && pdlBriefActive
      ? buildPdlDecisionTabNarrative({
          decisionEngine: decisionEngineForTab,
          declineReason: declineReasonForTab,
          applicationStatus,
        })
      : narrative;
  const showWhatsHappeningBanner = isPdl ? pdlBriefActive : true;

  const linkageMismatchAlert =
    application &&
    decisionEngine?.powerId &&
    application.legacyLinkage?.powerId &&
    decisionEnginePowerIdMismatch(application.legacyLinkage, decisionEngine.powerId) ? (
      <Alert
        severity="warning"
        action={
          onNavigate ? (
            <Button color="inherit" size="small" onClick={() => onNavigate('identity')}>
              Open Identity
            </Button>
          ) : undefined
        }
      >
        {formatDecisionEngineMismatchCopy(decisionEngine.powerId, application.legacyLinkage.powerId)}
      </Alert>
    ) : null;

  const decisionEnginePanel = (
    <ApplicationDecisionEnginePanel
      decisionEngine={decisionEngine}
      incomeRecords={incomeRecords}
      productType={application?.productType}
      applicationDeclineReason={declineReason}
      applicationStatus={applicationStatus}
      onNavigate={onNavigate}
    />
  );

  const decisionEngineSection =
    isPdl && pdlBriefActive ? (
      <PdlDecisionTourProvider>
        {decisionEnginePanel}
        <PdlDecisionReviewPathTour />
      </PdlDecisionTourProvider>
    ) : (
      decisionEnginePanel
    );

  if (executions.length === 0) {
    return (
      <Stack spacing={2}>
        {decidedRecord}
        {showWhatsHappeningBanner ? (
          <TabWhatsHappeningBanner narrative={whatsHappeningNarrative} onNavigate={onNavigate} />
        ) : null}
        {linkageMismatchAlert}
        {decisionEngineSection}
        <ApplicationFusedCapacityPanel fusedCapacity={decisionEngine?.fusedCapacity} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {decidedRecord}
      {showWhatsHappeningBanner ? (
        <TabWhatsHappeningBanner narrative={whatsHappeningNarrative} onNavigate={onNavigate} />
      ) : null}
      {linkageMismatchAlert}
      {decisionEngineSection}
      <ApplicationFusedCapacityPanel fusedCapacity={decisionEngine?.fusedCapacity} />
      <ApplicationSectionCard
        title="Doctrine runs"
        subtitle="Each row is one automated underwriting run. Transport = whether the run finished; Outcome = lending signal. Hover chips for plain-language help."
        icon={<HistoryRoundedIcon fontSize="small" />}
        endAdornment={
          <Chip
            size="small"
            variant="outlined"
            label={`${executions.length} run${executions.length === 1 ? '' : 's'}`}
            sx={{ height: 22, fontWeight: 700 }}
          />
        }
      >
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            bgcolor: (t) =>
              t.palette.mode === 'light'
                ? alpha(t.palette.common.black, 0.015)
                : alpha(t.palette.common.white, 0.03),
          }}
        >
          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === 'light'
                        ? alpha(t.palette.primary.main, 0.04)
                        : alpha(t.palette.common.white, 0.04),
                  }}
                >
                  {['Doctrine', 'Transport', 'Outcome', 'Steps', 'Window', 'Execution id'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 800,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.65rem',
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.map((ex) => {
                  const failedSteps = ex.steps.filter((s) => s.status !== 'OK').length;
                  const okSteps = ex.steps.length - failedSteps;
                  return (
                    <TableRow
                      key={ex.id}
                      sx={{
                        '&:hover': {
                          bgcolor: (t) =>
                            alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.04 : 0.08),
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{ex.doctrineId}</TableCell>
                      <TableCell>
                        <Tooltip title={doctrineTransportPlain(ex.status)} arrow>
                          <Chip size="small" label={ex.status} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {ex.outcome != null ? (
                          <Tooltip title={doctrineOutcomePlain(ex.outcome)} arrow>
                            <Chip
                              size="small"
                              label={ex.outcome}
                              color="primary"
                              sx={{ height: 22, fontWeight: 700 }}
                            />
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {ex.steps.length === 0 ? (
                          '—'
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {okSteps} ok
                            {failedSteps ? ` · ${failedSteps} issue(s)` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" display="block" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {new Date(ex.startedAt).toLocaleString()}
                        </Typography>
                        {ex.endedAt ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            → {new Date(ex.endedAt).toLocaleString()}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                        >
                          {ex.id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </ApplicationSectionCard>
    </Stack>
  );
}
